/**
 * ============================================================================
 * EverythingAnswered — View
 * ============================================================================
 * Purpose: Presentational UI for homepage FAQ. No data fetching / mapping.
 * ============================================================================
 */

"use client";

import React, { useEffect, useRef } from "react";
import UnivoImage from "@/components/common/UnivoImage";
import { TAB_HOVER_ICONS, TAB_ICONS } from "./FaqAudienceIcons";
import { useScrollToHashSection } from "@/helpers/useScrollToHashSection";
import styles from "./EverythingAnswered.module.scss";
import type {
  EverythingAnsweredViewModel,
  FaqQuestion,
} from "../model/everything-answered.types";

const toOpaqueColor = (color: string | null | undefined) => {
  if (!color) return color;
  const hex8 = color.match(/^#([0-9a-fA-F]{6})[0-9a-fA-F]{2}$/);
  if (hex8) return `#${hex8[1]}`;
  const rgba = color.match(/^rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)$/);
  if (rgba) return `rgb(${rgba[1]},${rgba[2]},${rgba[3]})`;
  return color;
};

const Answer = ({ answer }: { answer: string }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    let cancelled = false;
    const container = ref.current;
    container.innerHTML = "";
    const parser = new DOMParser();
    const doc = parser.parseFromString(answer, "text/html");

    const typeNode = async (
      source: Node,
      target: Node
    ): Promise<void> => {
      if (cancelled) return;

      if (source.nodeType === Node.TEXT_NODE) {
        const parts = (source.textContent || "").split(/(\s+)/);
        let typed = "";

        for (const part of parts) {
          if (cancelled) return;

          typed += part;
          target.textContent = typed;

          if (part.trim()) await new Promise((r) => setTimeout(r, 40));
        }
      } else if (source.nodeType === Node.ELEMENT_NODE) {
        const element = source as HTMLElement;
        const clone = document.createElement(element.tagName);

        Array.from(element.attributes).forEach((attr) =>
          clone.setAttribute(attr.name, attr.value)
        );

        target.appendChild(clone);

        for (const child of Array.from(element.childNodes)) {
          if (child.nodeType === Node.TEXT_NODE) {
            const textNode = document.createTextNode("");
            clone.appendChild(textNode);

            await typeNode(child, textNode);
          } else {
            await typeNode(child, clone);
          }
        }
      }
    };

    (async () => {
      for (const child of Array.from(doc.body.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
          const textNode = document.createTextNode("");
          container.appendChild(textNode);
          await typeNode(child, textNode);
        } else {
          await typeNode(child, container);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [answer]);

  return <div className={styles.answerBox} ref={ref} />;
};

const QuestionBubble = React.memo(function QuestionBubble({
  question,
  className,
  onSelect,
}: {
  question: FaqQuestion;
  className: string;
  onSelect: (q: FaqQuestion) => void;
}) {
  const style = question.borderColor
    ? ({
        ["--bubble-hover-color" as string]: toOpaqueColor(question.borderColor),
        boxShadow: `
                  0 0 6px ${question.borderColor},
                  0 0 12px ${question.borderColor},
                  0 4px 10px ${question.borderColor}
                `,
      } as React.CSSProperties)
    : {};

  return (
    <button className={className} style={style} onClick={() => onSelect(question)}>
      <span className={styles.bubbleText}>{question.text}</span>
    </button>
  );
});

const FaqModal = React.memo(function FaqModal({
  question,
  allQuestions,
  onClose,
  onRelated,
  onCounsellor,
  onPrograms,
}: {
  question: FaqQuestion;
  allQuestions: FaqQuestion[];
  onClose: () => void;
  onRelated: (id: string) => void;
  onCounsellor: () => void;
  onPrograms: () => void;
}) {
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
          ✕
        </button>

        <h3 className={styles.modalTitle}>{question.text}</h3>

        {question.answer && <Answer answer={question.answer} />}

        {question.relatedIds.length > 0 && (
          <div className={styles.relatedSection}>
            <p className={styles.relatedLabel}>You might also have these questions-</p>
            <div className={styles.relatedPills}>
              {question.relatedIds.map((rId) => {
                const rQ = allQuestions.find((q) => q.id === rId);
                return rQ ? (
                  <button
                    key={rId}
                    className={styles.pill}
                    onClick={() => onRelated(rId)}
                  >
                    {rQ.text}
                  </button>
                ) : null;
              })}
            </div>
          </div>
        )}

        <div className={styles.modalCtas}>
          <button className={styles.ctaPrimary} onClick={onCounsellor}>
            Talk to a Counsellor
          </button>
          <button onClick={onPrograms} className={styles.ctaSecondary}>
            Explore Programs
          </button>
        </div>
      </div>
    </div>
  );
});

export default function EverythingAnsweredView({
  heading,
  rawPersonas,
  activeTab,
  activeQuestion,
  currentPersona,
  leftQuestions,
  rightQuestions,
  handleTabChange,
  openModal,
  closeModal,
  handleRelated,
  handleCounsellor,
  handlePrograms,
}: EverythingAnsweredViewModel) {
  const sectionRef = useRef<HTMLElement>(null);

  useScrollToHashSection(sectionRef, "#question-answer");

  return (
    <section className={styles.section} id="question-answer" ref={sectionRef}>
      <div className={styles.titleRow}>
        <div className={styles.title} dangerouslySetInnerHTML={{ __html: heading }} />
      </div>

      <div className={styles.card}>
        <div className={styles.tabBar}>
          {rawPersonas.map((persona, i) => {
            const Icon = activeTab === i ? TAB_HOVER_ICONS[i] : TAB_ICONS[i];
            return (
              <button
                key={i}
                className={`${styles.tab} ${activeTab === i ? styles.tabActive : ""} ${i == 0 ? "rounded-l-lg" : i == 3 ? "rounded-r-lg" : ""}`}
                onClick={() => handleTabChange(i)}
              >
                <Icon />
                <span>{persona.EnumHeading}</span>
              </button>
            );
          })}
        </div>

        <div
          key={activeTab}
          className={`${styles.contentArea} ${styles.contentAreaEnter}`}
        >
          <div className={styles.bubblesCol}>
            {leftQuestions?.map((q, idx) => (
              <QuestionBubble
                key={q.id ?? idx}
                question={q}
                className={`left ${styles.bubble} ${styles.bubbleLeft} ${styles[`leftOffset${idx}`]}`}
                onSelect={openModal}
              />
            ))}
          </div>

          <div className={styles.imageCol}>
            <UnivoImage
              src={currentPersona?.image}
              alt={currentPersona?.tab || "Learner persona"}
              className={styles.personImage}
              width={880}
              height={560}
              sizes="(max-width: 1280px) 50vw, 880px"
              loading="lazy"
              fetchPriority="low"
            />
          </div>

          <div className={`${styles.bubblesCol} ${styles.bubblesColRight}`}>
            {rightQuestions?.map((q, idx) => (
              <QuestionBubble
                key={q.id ?? idx}
                question={q}
                className={`${styles.bubble} ${styles.bubbleRight} ${styles[`rightOffset${idx}`]}`}
                onSelect={openModal}
              />
            ))}
          </div>
        </div>
      </div>

      {activeQuestion && (
        <FaqModal
          question={activeQuestion}
          allQuestions={currentPersona?.allQuestions ?? []}
          onClose={closeModal}
          onRelated={handleRelated}
          onCounsellor={handleCounsellor}
          onPrograms={handlePrograms}
        />
      )}
    </section>
  );
}
